// ** MUI Imports
import { debounce } from '@mui/material'

// ** Child Imports
import NewDatePicker from 'src/components/common/search-and-filters/subcomponents/NewDatePicker'

// ** Third Party Styles Imports
import 'react-datepicker/dist/react-datepicker.css'

// ** Context
import { useFilter } from 'src/lib/contexts/FilterContext'

// ** Types
import { FilterDataType, FilterParentType, TabType } from 'src/lib/types/filters'

// ** Helpers
import { dashboardTabs } from 'src/lib/utils/constants'
import { appliedDateFilter, getDateTime } from 'src/lib/utils/helpers'

import OverviewLayoutBuilder from 'src/components/analytics-dashboard/OverviewLayoutBuilder'
import { X } from 'react-feather'
import FloFilters from 'src/components/common/filter-component/FloFilters'
import { extractDateFromFilters } from 'src/lib/utils/analyticsHelpers'
import { initialOverviewFilterData, overviewFilterConfig } from 'src/assets/AnalyticsConfig'
import { useCallback, useEffect, useMemo, useState } from 'react'
import SyncStatus from 'src/components/sso-analytics/common/SyncStatus'
import produce from 'immer'
import { useLocale } from 'src/lib/hooks/useLocal'
import BlockedPermissions from 'src/layouts/components/BlockedPermissions'
import { useUser } from 'src/lib/contexts/UserContext'
import { useDateFilters } from 'src/lib/hooks/useDateFilters'

const AnalyticsDashboard = () => {
  const { t } = useLocale()
  const { analyticsFilters, setAnalyticsFiltersCtx } = useFilter()

  //Filter data (In our case it will represent the column and its values)
  const [filterData, setFilterData] = useState<FilterDataType[]>(initialOverviewFilterData)
  const [isSyncing, setIsSyncing] = useState(false)
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null)

  useEffect(() => setLastSyncedAt(new Date()), [])
  // const { dateFilter, setDateFilterCtx } = useDateFilters(dashboardTabs.FLO_ANALYTICS_TAB as FilterParentType)
  const handleDeleteColumnValue = (columnValue: string, filterIndex: number) => {
    const modifiedFilter = { ...filterData[filterIndex] }
    const deletedValueIndex = modifiedFilter.values.findIndex((value: any) => value?.id === columnValue)
    const modifiedValues = [...modifiedFilter.values]
    modifiedValues.splice(deletedValueIndex, 1)
    modifiedFilter.values = modifiedValues

    const modifiedFilterData = [...filterData]
    modifiedFilterData[filterIndex] = modifiedFilter

    setFilterData(
      produce((draft: any) => {
        draft[filterIndex] = modifiedFilter
      })
    )
  }

  const renderLayoutBuilder = () => (
    <OverviewLayoutBuilder customFilters={filterData?.[0]?.values?.map((filterValue: any) => filterValue.id)} />
  )
  const { hasAllTabPermissions } = useUser()

  const handleRefresh = debounce(() => {
    setIsSyncing(true)
    setLastSyncedAt(new Date())
    setAnalyticsFiltersCtx({
      ...analyticsFilters,
      lastUpdated: `${getDateTime(new Date()).date} at ${getDateTime(new Date()).time}`
    })
    window.setTimeout(() => setIsSyncing(false), 1500)
  }, 300)

  return (
    <div
      className='p-8'
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16
      }}
    >
      <div className='flex flex-col gap-2 px-4 py-b-4 md:p-0'>
        <div className='flex items-center justify-between gap-2'>
          <div className='flex items-center gap-2'>
            <h1 className='text-text-lg-semibold text-zinc-800'>Overview</h1>
            <SyncStatus lastSyncedAt={lastSyncedAt} isSyncing={isSyncing} onRefresh={handleRefresh} />
          </div>
          <div className='flex items-center gap-3'>
            <FloFilters
              startDate={extractDateFromFilters(analyticsFilters, 'startDate')}
              endDate={extractDateFromFilters(analyticsFilters, 'endDate')}
              filterConfig={overviewFilterConfig}
              initialFilterData={initialOverviewFilterData}
              filterArrayData={filterData}
              setFilterArrayData={setFilterData}
              isQueryBasedFilter={false}
              buttonClassName='flex items-center gap-1 px-3 py-2 text-sm font-semibold transition duration-200 bg-transparent border rounded whitespace-nowrap h-[30px] border-zinc-200 hover:bg-zinc-50'
            />
            <NewDatePicker
              dateFilter={analyticsFilters}
              setDateFilterCtx={setAnalyticsFiltersCtx}
              appliedFilter={appliedDateFilter(analyticsFilters)}
              minDateValue={new Date(2022, 8, 20)}
              maxDateValue={new Date()}
              parentComponent={dashboardTabs.FLO_ANALYTICS_TAB as TabType}
              comparison
              hasCustomComparisons
            />
          </div>
        </div>
      </div>
      <div className='flex flex-wrap w-full h-full gap-2'>
        {filterData?.map((data: FilterDataType, index: number) => {
          return data?.values?.map((value: any, valueIndex: number) => (
            <div
              className='rounded-lg text-sm bg-[#FAFAFA] text-[#6E6E6E] border border-[#F0F0F0] flex px-2 py-1 items-center gap-2'
              key={`${valueIndex}_${value?.id}`}
            >
              <span className='text-sm text-coal-dark'>
                Channel is <span className='text-sm font-medium text-primary-dark'>{value?.name}</span>
              </span>
              <X
                className='stroke-[2.5px] text-[#6e6e6e] w-4 h-4 mt-[2px] cursor-pointer'
                onClick={() => handleDeleteColumnValue(value?.id, index)}
              />
            </div>
          ))
        })}
      </div>
      {renderLayoutBuilder()}

      {!hasAllTabPermissions('TAB_OVERVIEW') && (
        <div className='mt-4'>
          <BlockedPermissions title={'Unlock more Analytics'} />
        </div>
      )}
    </div>
  )
}

export default AnalyticsDashboard
